import { ReactElement } from 'react';
import { type Options } from '@popperjs/core';
import { useEffectOnce } from 'react-use';
import { useIsTabbarHidden } from '../../../hooks/useIsTabbarHidden';
import { formatText } from '../../../utils/format/formatText';
import { useIsMobile } from '../../../utils/getIsMobile';
import { ymTrackCreateCollectionOnboardingShown } from '../../../utils/metrika';
import { useExperimentFlag } from '../../../utils/useExperimentFlag';
import { useTranslation } from '../../../utils/useTranslation';
import { Typography } from '../../typography/Typography';
import { Tooltip } from '../Tooltip';
import { useIsVisible } from './hooks/useIsVisible';
import { useScrollDetection } from './hooks/useScrollDetection';

interface CreateCollectionOnboardingProps {
    children: ReactElement;
    options?: Omit<Partial<Options>, 'modifiers'>;
}

export const CreateCollectionOnboarding = ({
    children,
    options,
}: CreateCollectionOnboardingProps) => {
    const { onClose, isVisible } = useIsVisible();
    const t = useTranslation();
    const isMobile = useIsMobile();
    const hasScrolled = useScrollDetection();
    const isEnabled =
        String(useExperimentFlag('create_collection_from_plus_onboarding_enabled')) === '1';

    useEffectOnce(() => {
        ymTrackCreateCollectionOnboardingShown();
    });

    const shouldHideMobileTabbar = useIsTabbarHidden();

    if (
        !isEnabled ||
        !isVisible ||
        (shouldHideMobileTabbar && isMobile) ||
        isMobile === null ||
        (isMobile && hasScrolled)
    ) {
        return children;
    }

    return (
        <Tooltip
            options={options}
            variant="onboarding"
            onClose={onClose}
            isVisible={isVisible}
            trigger={children}
            disableHoverEvents
        >
            <Typography py={0.5} variant="body" color="var(--color-invert)">
                {formatText(
                    t(
                        'Теперь вы можете создавать свои коллекции, которые увидят другие пользователи',
                    ),
                )}
            </Typography>
        </Tooltip>
    );
};
